import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Engineering as EngineeringIcon,
  Visibility as VisibilityIcon,
  Login as Login,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import type { TeamUserRole, TeamUserTableData } from '../types';
import { red } from '@mui/material/colors';

interface TeamUserTableProps {
  teamUsers: TeamUserTableData[];
  onEdit: (teamUser: TeamUserTableData) => void;
  onDelete: (teamUser: TeamUserTableData) => void;
  isLoading?: boolean;
}

export const TeamUserTable: React.FC<TeamUserTableProps> = ({
  teamUsers,
  onEdit,
  onDelete,
  isLoading = false,
}) => {
  const { user } = useAuth();
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    user: TeamUserTableData | null;
  }>({ open: false, user: null });

  const getRoleIconComponent = (role: TeamUserRole) => {
    switch (role) {
      case 'ADMIN_USER':
        return <AdminIcon fontSize="small" />;
      case 'OPS_USER':
        return <EngineeringIcon fontSize="small" />;
      case 'VIEWER_USER':
        return <VisibilityIcon fontSize="small" />;
      default:
        return <PersonIcon fontSize="small" />;
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const handleLogin = (teamUser: any) => {
    if (!teamUser?.uuid) return; // safety check

    // 👇 Example login URL — update this to match your backend route
    const loginUrl = `${window.location.origin}/auth/team-login/${user?.uuid}`;

    // Open in a new tab
    window.open(loginUrl, '_blank', 'noopener,noreferrer');
  };

  const handleCopy = (teamUser: any) => {
    if (!teamUser?.uuid) return; // safety check

    const loginUrl = `${window.location.origin}/auth/team-login/${user?.uuid}`;
    navigator.clipboard.writeText(loginUrl);
  };

  if (isLoading) {
    return (
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Account</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>2FA</TableCell>
                <TableCell>Last sign in</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress size={24} sx={{ mr: 2 }} />
                    <Typography variant="body1" color="textSecondary">
                      Loading team users...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>

            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  if (teamUsers.length === 0) {
    return (
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Account</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>2FA</TableCell>
                <TableCell>Last sign in</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    No team users found matching your search criteria.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                Account
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                Name
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                Email
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                Role
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                Status
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                2FA
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                Last sign in
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teamUsers.map((teamUser) => (
              <TableRow key={teamUser.uuid} hover>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    {teamUser.account || teamUser.merchant_name || 'N/A'}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {teamUser.name}
                    {user?.email === teamUser.email && (
                      <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                        (You)
                      </Typography>
                    )}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    {teamUser.email}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    {teamUser.role_name || 'N/A'}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Chip
                    icon={teamUser.is_active ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : undefined}
                    label={teamUser.is_active ? 'Active' : 'Inactive'}
                    size="small"
                    color={teamUser.is_active ? 'success' : 'default'}
                    sx={{
                      fontWeight: 'normal',
                      fontSize: '0.75rem',
                      borderRadius: '12px',
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                </TableCell>

                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    {teamUser.two_factor_auth || '-'}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    {formatDate(teamUser.last_login)}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Edit User">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onEdit(teamUser)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete User">
                      <IconButton
                        size="small"
                        color="error"

                        onClick={() => setDeleteDialog({ open: true, user: teamUser })}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Login as User">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleLogin(teamUser)}
                      >
                        <Login fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Copy Login URL">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleCopy(teamUser)}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, user: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Remove team member
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to remove <strong>{deleteDialog.user?.name}</strong> from your team?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. The user will lose access to all team resources and data.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, user: null })}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (deleteDialog.user) {
                onDelete(deleteDialog.user);
                setDeleteDialog({ open: false, user: null });
              }
            }}
            variant="contained"
            sx={{
              color: 'white',
              
            }}
          >
            Remove member
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
